import { Fragment, memo, useState } from 'react';

import { Plus } from 'lucide-react';

import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

import css from './UserRolesFormControl.module.css';

import { UserRole, userRoleToUIMap, USER_ROLES } from 'v2/constants';
import { Nullable } from 'v2/types';
import { areArraysEqual } from 'v2/utils';

interface UserRolesFormControlProps {
  roles: UserRole[];
  onDelete: (role: UserRole) => void;
  onUpdate: (roles: UserRole[]) => void;
}

export const UserRolesFormControl = memo<UserRolesFormControlProps>(({ roles, onUpdate, onDelete }) => {
  const [anchorEl, setAnchorEl] = useState<Nullable<HTMLElement>>(null);

  const [updatedRoles, setUpdatedRoles] = useState(roles);

  const handleSelectRole = (role: UserRole) => () => {
    setUpdatedRoles((prev) => {
      if (prev.includes(role)) {
        return prev.filter((r) => role !== r);
      } else {
        return [...prev, role];
      }
    });
  };

  const handleUpdateRoles = () => {
    if (areArraysEqual(updatedRoles, roles)) {
      handleClose();
    } else {
      onUpdate(updatedRoles);
      handleClose();
    }
  };

  const handleDeleteRole = (r: UserRole) => () => {
    onDelete(r);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div className={css.userRoles}>
      {roles.map((r, _, arr) => (
        <Chip
          key={r}
          label={userRoleToUIMap[r]}
          color="primary"
          variant="outlined"
          size="small"
          onDelete={arr.length > 1 ? handleDeleteRole(r) : undefined}
        />
      ))}

      {roles.length < USER_ROLES.length && (
        <Fragment>
          <Chip icon={<Plus width={16} />} label="Add..." onClick={(e) => setAnchorEl(e.currentTarget)} size="small" />

          <Menu open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={handleUpdateRoles}>
            {USER_ROLES.map((r) => (
              <MenuItem key={r} value={r} onClick={handleSelectRole(r)}>
                <Checkbox checked={updatedRoles.includes(r)} />
                {r}
              </MenuItem>
            ))}
          </Menu>
        </Fragment>
      )}
    </div>
  );
});

UserRolesFormControl.displayName = 'UserRolesFormControl';
